import { useEffect, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';

import ELEMENT from 'common/element';
import Page from 'components/common/Page/Page';
import * as K from 'common/constants';
import { createErrorMessage, ERROR_CODE } from 'common/errors';
import { getRedirectUrl } from 'common/util/auth';
import { gqlRequest } from 'common/util/gqlRequest';
import { buildAbsoluteUrl, getUrlParam } from 'common/util/location';
import { DEFAULT_THEME, setUITheme } from 'service/browser';

import PageHeader from 'components/common/PageHeader/PageHeader';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';

import { getRememberMeValue } from './AuthPageFooter';
import { fetchCSRFToken } from './authAPI';
import { AUTH_PAGE_LOGIN_TYPE, LOGIN_ERROR_MESSAGES, XSRF_NOT_LOGGED_IN } from './constants';
import PasswordLogin from './PasswordLogin';
import { REQUEST_PASSWORD_RESET } from './queries';
import SSOLogin from './SSOLogin';
import SSOLoginAs from './SSOLoginAs';

import './AuthPage.scss';

export type AuthPageFormValues = {
  email: string;
  password: string;
  rememberMe: boolean;
};

const isValidLoginType = (loginTypeValue: string | null) =>
  loginTypeValue === AUTH_PAGE_LOGIN_TYPE.SSO || loginTypeValue === AUTH_PAGE_LOGIN_TYPE.EMAIL_PASSWORD;

const getInitialLoginType = () => {
  if (getRememberMeValue()) {
    const loginTypeFromLocalStorage = localStorage.getItem('loginType');
    return isValidLoginType(loginTypeFromLocalStorage)
      ? (loginTypeFromLocalStorage as AUTH_PAGE_LOGIN_TYPE)
      : AUTH_PAGE_LOGIN_TYPE.SSO;
  }
  return AUTH_PAGE_LOGIN_TYPE.SSO;
};

const getInitialUserEmail = () => {
  if (getRememberMeValue()) {
    const userEmailFromLocalStorage = localStorage.getItem('userEmail');
    return userEmailFromLocalStorage ? userEmailFromLocalStorage : '';
  }
  return '';
};

function AuthPage(): JSX.Element {
  const [status, setStatus] = useState<K.COMPONENT_STATUS>(K.COMPONENT_STATUS.IDLE);
  const [error, setError] = useState<Error>();
  const [xsrfToken, setXsrfToken] = useState<string>(XSRF_NOT_LOGGED_IN); // only used for SSO Login,
  const [renderForm, setRenderForm] = useState<boolean>(false);
  const formRef = useRef<{ values: AuthPageFormValues }>();
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  // loginType, userEmail, rememberMe can be saved by user to localstorage
  const [loginType, setLoginType] = useState<AUTH_PAGE_LOGIN_TYPE>(getInitialLoginType);
  const [userEmail, setUserEmail] = useState<string>(getInitialUserEmail);

  /**
   * Checking if user is in verifyEmail path
   */
  function usePageAlertIfUserInVerifyEmail(): void {
    useEffect(() => {
      if (window.location.href.includes('verifyEmail')) {
        /* If we are inside this conditional block, user is most likely coming from the welcome email and is activating their new account
         * by opening the link in a different browser or their session has expired.
         * Since verifyEmail route is a private route, user is taken to the login page if their session expired
         * We will notify the user that they need to login to complete the verification
         */
        setPageAlertConfig({
          message: 'To complete the account registration process, please login with your email address and password',
          variant: PageAlertVariant.SUCCESS,
          autoHide: false,
        });
      }
    }, []);
  }

  /**
   * Handles `originalUrl` initialization
   */
  function useOriginalUrlWatcher(): void {
    /**
     * If the current route is a private route – initialize `originalUrl`, so we can redirect user back after authentication
     */
    useEffect(() => {
      if (new URLSearchParams(window.location.search).get(K.URL_PARAM.AUTH_ORIGINAL_URL)) {
        return; // `originalUrl` already in place
      }
      if (window.location.pathname.includes(K.ROUTE.LOG_IN)) {
        return; // `login` route itself should not be an `originalUrl`
      }

      const originalUrl = buildAbsoluteUrl(window.location.toString());
      window.location.href = `${K.ROUTE.LOG_IN}?${K.URL_PARAM.AUTH_ORIGINAL_URL}=${originalUrl}`;
    }, []);
  }
  /**
   * If user ends up on /login route but if they are already logged in, redirect to home page and don't render the form
   */
  function useRedirectUserIfAlreadyLoggedIn(userEmailParam: string): void {
    useEffect(() => {
      fetchCSRFToken()
        .then((token) => {
          if (token && token !== XSRF_NOT_LOGGED_IN && !userEmailParam) {
            setRenderForm(false);
            window.location.href = getRedirectUrl();
          } else {
            setRenderForm(true);
          }
        })
        .catch(() => {
          setRenderForm(true);
          setPageAlertConfig({
            message: createErrorMessage(ERROR_CODE.CSRF_TOKEN_FETCHING_ERROR),
            variant: PageAlertVariant.ERROR,
            autoHide: false,
          });
        });
    }, []);
  }

  /**
   * Set theme based on user's system preference
   */

  function useUserPreferredTheme() {
    useEffect(() => {
      setUITheme(DEFAULT_THEME);
    }, []);
  }

  const errorParamValue = getUrlParam(window.location.search, K.URL_PARAM.ERR);
  const customErrorMessage = getUrlParam(window.location.search, K.URL_PARAM.ERR_MSG);
  const userEmailParamValue = getUrlParam(window.location.search, K.URL_PARAM.USER_EMAIL);

  function useErrorParameter() {
    /*
     * Handle error from server if error parameter is present, err parameter will be set by backend service only for SSO Login
     * An example of the url with error parameter: /login?err=invalidToken
     */
    useEffect(() => {
      if (!error?.message && errorParamValue) {
        const errorFromParam: Error = { message: errorParamValue, name: errorParamValue };
        // Set error if error is not previously set
        setError(errorFromParam);
        setStatus(K.COMPONENT_STATUS.REJECTED);
      }
    }, []);
  }

  function useUserEmailParameter() {
    /*
     * If error param is absent but has a userEmail param present, this will be set only for SSO Login
     * It is the case when user has used the SSO idP site (For example: Okta) directly to login to Scalyr app
     * Then, the user is technically logged in but they don't have an xsrf token yet.
     * So, they land on a page with their email id and a continue button (this is the SSOLoginAs form)
     * We fetch the xsrfToken and when they click the continue button, we send it to server,
     * server validates and completes the login process.
     */
    useEffect(() => {
      if (loginType !== AUTH_PAGE_LOGIN_TYPE.SSO_LOGIN_AS && !errorParamValue && userEmailParamValue) {
        fetchCSRFToken()
          .then((token) => {
            if (token && token !== XSRF_NOT_LOGGED_IN) {
              setLoginType(AUTH_PAGE_LOGIN_TYPE.SSO_LOGIN_AS);
              setXsrfToken(token);
            }
          })
          .catch(() => {});
      }
    }, []);
  }

  useRedirectUserIfAlreadyLoggedIn(userEmailParamValue);
  useOriginalUrlWatcher();
  useUserPreferredTheme();
  usePageAlertIfUserInVerifyEmail();

  // URL parameters will only be set for SSO Login. For password login, error will be sent by the GQL query
  useErrorParameter();
  useUserEmailParameter();

  // This will be triggered when user clicks 'Forgot Password', will make a GQL query, display errors, if any or display successful message
  async function requestPasswordReset(): Promise<void> {
    let email = formRef.current?.values?.email;

    if (!email) {
      email = window.prompt('What is your e-mail address?') || ''; // eslint-disable-line no-alert
    }

    if (email) {
      try {
        setStatus(K.COMPONENT_STATUS.PENDING);
        await gqlRequest({ query: REQUEST_PASSWORD_RESET, variables: { email } });
        setStatus(K.COMPONENT_STATUS.RESOLVED);
      } catch (err: any) {
        setStatus(K.COMPONENT_STATUS.REJECTED);
      }
      setPageAlertConfig({
        message: 'You will be receiving a password reset email if this email account exists',
        variant: PageAlertVariant.INFO,
        autoHide: false,
      });
    }
  }

  // This method sets the correct stage for the login page depending on which login method user has chosen
  // This will render the appropriate login form based on user's choice
  function changeAuthMethod(): void {
    switch (loginType) {
      case AUTH_PAGE_LOGIN_TYPE.EMAIL_PASSWORD:
        setLoginType(AUTH_PAGE_LOGIN_TYPE.SSO);
        break;
      case AUTH_PAGE_LOGIN_TYPE.SSO:
        setLoginType(AUTH_PAGE_LOGIN_TYPE.EMAIL_PASSWORD);
        break;
    }
    setStatus(K.COMPONENT_STATUS.IDLE);
  }

  // This method will process the error and display on the page
  function renderErrorMessage(error: Error): string {
    const errorMessagesWithoutType = [
      'is not registered',
      'email address entered is associated with an SSO domain',
      'Please specify an email address',
      'Something went wrong. Please try again',
    ];
    if (error && error.message) {
      if (errorMessagesWithoutType.find((errMessage) => errMessage.includes(error.message))) {
        return error.message;
      }
      if (error.name === 'noAccess') {
        // user is denied access, render custom error message if available
        return customErrorMessage ? customErrorMessage : LOGIN_ERROR_MESSAGES.noAccess;
      }
      if (Object.keys(LOGIN_ERROR_MESSAGES).includes(error.message)) {
        return LOGIN_ERROR_MESSAGES[error.message];
      }
    }
    return `${LOGIN_ERROR_MESSAGES.unknownError}`;
  }

  return renderForm ? (
    <>
      <Page docTitle="Log In" pageModifier="AuthPage" type="full" testId={ELEMENT.authPage}>
        <div className="AuthPage__Container">
          {status === K.COMPONENT_STATUS.REJECTED && error && (
            <div className="AuthPage__ErrorMessage">{error && renderErrorMessage(error)}</div>
          )}
          <PageHeader title={loginType !== AUTH_PAGE_LOGIN_TYPE.SSO_LOGIN_AS ? 'Log In' : 'Logging in as'} />
          {loginType === AUTH_PAGE_LOGIN_TYPE.EMAIL_PASSWORD && (
            <PasswordLogin
              changeAuthMethod={changeAuthMethod}
              getRedirectUrl={getRedirectUrl}
              formRef={formRef}
              setError={setError}
              setStatus={setStatus}
              status={status}
              emailToDisplay={userEmail}
              requestPasswordReset={requestPasswordReset}
            />
          )}
          {loginType === AUTH_PAGE_LOGIN_TYPE.SSO && (
            <SSOLogin
              changeAuthMethod={changeAuthMethod}
              getRedirectUrl={getRedirectUrl}
              setStage={setLoginType}
              setError={setError}
              setStatus={setStatus}
              status={status}
              setEmailForPassword={setUserEmail}
              emailToDisplay={userEmail}
            />
          )}
          {loginType === AUTH_PAGE_LOGIN_TYPE.SSO_LOGIN_AS && (
            <SSOLoginAs userEmail={userEmailParamValue} xsrfToken={xsrfToken} />
          )}
        </div>
      </Page>
    </>
  ) : (
    <></>
  );
}

export default AuthPage;
